import React, { useState } from "react";
import { ChevronDown, ChevronRight, Lightbulb, ShieldCheck, AlertTriangle } from "lucide-react";

/**
 * Expandable "Show Reasoning" panel attached to every AI output.
 * Per ARIA spec: Sources consulted | Confidence (High / Medium / Low) | Known limitations.
 *
 * Accepts either a backend-provided `reasoning` payload (preferred — produced by
 * topology._build_reasoning) or falls back to display whatever fields are passed.
 */
export default function ShowReasoning({ reasoning, score, testid = "show-reasoning" }) {
  const [open, setOpen] = useState(false);

  const confidence = reasoning?.confidence || (
    score >= 85 ? "High" : score >= 70 ? "Medium" : "Low"
  );
  const sources = reasoning?.sources || [
    "ZidaMD clinical knowledge base (Claude Sonnet 4.5)",
    "Encounter context supplied at request time",
  ];
  const limitations = reasoning?.limitations || [
    "Output is not a substitute for professional clinical judgment.",
    "Codes and policy citations should be confirmed against current payer guidelines.",
  ];
  const plan = reasoning?.plan_outline;
  const critique = reasoning?.self_critique;

  const confColor = confidence === "High" ? "text-emerald-700 bg-emerald-50 border-emerald-200"
    : confidence === "Medium" ? "text-amber-700 bg-amber-50 border-amber-200"
    : "text-red-700 bg-red-50 border-red-200";

  return (
    <div className="mt-3 border border-brand rounded-md" data-testid={testid}>
      <button type="button" onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-3 py-2 text-xs hover:bg-brand-bg transition-colors"
        data-testid={`${testid}-toggle`}>
        <span className="flex items-center gap-2 text-brand-text font-medium">
          <Lightbulb className="w-3.5 h-3.5 text-brand-primary" />
          Show Reasoning
          <span className={`ml-2 px-2 py-0.5 rounded-full border text-[10px] font-semibold ${confColor}`}>
            Confidence: {confidence}
          </span>
        </span>
        {open ? <ChevronDown className="w-3.5 h-3.5 text-brand-muted" /> : <ChevronRight className="w-3.5 h-3.5 text-brand-muted" />}
      </button>
      {open && (
        <div className="px-3 pb-3 pt-1 space-y-3 text-xs text-brand-text" data-testid={`${testid}-body`}>
          <div>
            <div className="eyebrow mb-1.5 flex items-center gap-1.5">
              <ShieldCheck className="w-3 h-3" /> Sources consulted
            </div>
            <ul className="space-y-1 text-brand-muted">
              {sources.map((s, i) => <li key={i}>• {s}</li>)}
            </ul>
          </div>
          {plan && (
            <div>
              <div className="eyebrow mb-1.5">Planner outline</div>
              <pre className="whitespace-pre-wrap text-[11px] text-brand-muted leading-relaxed">{plan}</pre>
            </div>
          )}
          {critique && (
            <div>
              <div className="eyebrow mb-1.5">Self-critique</div>
              <p className="text-brand-muted leading-relaxed">{critique}</p>
            </div>
          )}
          <div>
            <div className="eyebrow mb-1.5 flex items-center gap-1.5">
              <AlertTriangle className="w-3 h-3 text-amber-600" /> Known limitations
            </div>
            <ul className="space-y-1 text-brand-muted">
              {limitations.map((l, i) => <li key={i}>• {l}</li>)}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}
